import { Dispatch, SetStateAction, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';

import { AxiosResponse } from 'axios';
import clsx from 'clsx';

import { Modal, SingleSelect } from '@/common';
import { PaginationArrow } from '@/common/PaginationArrow';
import { Search } from '@/components/common/Search';
import { Button, Label, LABEL_TYPE, Spinner } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { IMAGE, PAGE_SIZE, TEXT_OVERLAY, UPLOAD_PICKER_TYPES } from '@/constants';
import { AuthContext } from '@/contexts';
import {
  AssetScope,
  AssetType,
  ORGANIZATION_PERMISSION_TYPE,
  PERMISSION_TYPE,
  PRIVATE_PAGE_ROUTES
} from '@/enums';
import { TABS } from '@/enums/OrganizationAssetTabsEnum';
import { getAssetTypeForOrganizationAsset, getPermissionsForOrganization } from '@/helpers';
import { showToast, ToastType } from '@/helpers/toast';
import { useFileUpload, useUploadFilePicker } from '@/hooks';
import { ArrowDownBlackIcon as ArrowDownIcon, ArrowUpIcon } from '@/resources';
import {
  useCreateAssetCloneMutation,
  useDuplicateTextOverlayMutation,
  useGetAllOrganizations,
  useGetOrganizationByIdSimplifiedQuery,
  useGetTextOverlayByAssetIdMutation
} from '@/services';
import {
  useDeleteAssetMutation,
  useGetEventAssets,
  useGetOrganizationAssets
} from '@/services/asset.service';
import { useGetAllEvents } from '@/services/events.service';
import { useFilterStore } from '@/store';
import {
  Asset,
  AssetClonePayload,
  Event,
  Organization,
  SelectOptionProps,
  ThemeProp
} from '@/types';

import { AssetDetails } from '../Event/Asset/AssetDetails';
import { AssetItem } from '../Event/Asset/AssetItem';
import { SearchMusicModal } from './SearchMusicModal';

export const OrganizationAssets = () => {
  const {
    currentUserData,
    permissions: userPermissions,
    organizationPermissions
  } = useContext(AuthContext);
  const [t] = useTranslation('common');
  const { organizationFilter } = useFilterStore();
  const { ref, inView } = useInView();

  const [search, setSearch] = useState<string>();
  const [currentTab, setCurrentTab] = useState<string>(TABS.ALL_ASSET);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentAssetType, setCurrentAssetType] = useState<string>('');
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [assetDetailsLabel, setAssetDetailsLabel] = useState<string>();
  const [showAsset, setShowAsset] = useState<boolean>(false);
  const [isSearchMusicModalOpen, setIsSearchMusicModalOpen] = useState<boolean>(false);
  const [showOrgSelectModal, setShowOrgSelectModal] = useState<boolean>(false);
  const [orgSelectValue, setOrgSelectValue] = useState<SelectOptionProps | null>(null);
  const [ceremonySelectValue, setCeremonySelectValue] = useState<SelectOptionProps | null>();
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState<boolean>(false);
  const [selectedAssetForDuplicate, setSelectedAssetForDuplicate] = useState<Asset | null>(null);
  const [searchOrganization, setSearchOrganization] = useState('');
  const [textOverlayOptionSelected, setTextOverlayOptionSelected] = useState<boolean>(false);
  const [chooseCeremonyModalOpen, setChooseCeremonyModalOpen] = useState<boolean>(false);
  const [hasStartedBatchUpload, setHasStartedBatchUpload] = useState(false);
  const [selectedCeremonyAssets, setSelectedCeremonyAssets] = useState<Asset[]>([]);

  const navigate = useNavigate();

  const dropdownRef = useRef<HTMLDivElement>(null);

  const hasPermissionViewOrganizations = userPermissions.includes(
    PERMISSION_TYPE.VIEW_ORGANIZATIONS
  );

  const getOrganizationValue = () => {
    if (organizationFilter && organizationFilter.value) {
      return organizationFilter.value as number;
    }

    if (currentUserData?.organizations.length === 1) {
      return currentUserData?.organizations[0].id as number;
    }

    return undefined;
  };

  const targetOrganizationId = Number(orgSelectValue?.value ?? getOrganizationValue() ?? 0);
  const { data: targetOrganization, isFetching: isFetchingTargetOrganization } =
    useGetOrganizationByIdSimplifiedQuery(targetOrganizationId);
  const hasMediaLibraryAccess =
    !isFetchingTargetOrganization &&
    getPermissionsForOrganization(organizationPermissions, targetOrganization?.data).includes(
      ORGANIZATION_PERMISSION_TYPE.MEDIA_LIBRARY
    );
  const mediaLibraryMissingMessage =
    targetOrganizationId !== 0 && !isFetchingTargetOrganization && !hasMediaLibraryAccess
      ? t('yourOranizationDoesntHavePermission', {
          permission: t(ORGANIZATION_PERMISSION_TYPE.MEDIA_LIBRARY)
        })
      : undefined;

  const isBlockedWithoutMediaLibrary = () => {
    if (hasMediaLibraryAccess) {
      return false;
    }
    if (mediaLibraryMissingMessage) {
      showToast(ToastType.ERROR, mediaLibraryMissingMessage);
    }
    return true;
  };

  const { mutateAsync: createAssetClone } = useCreateAssetCloneMutation(queryClient, {
    onSuccess: (response?: AxiosResponse) => {
      organizationAssetsRefetch();
      showToast(
        ToastType.SUCCESS,
        t('assetSuccessfullyCreated', { assetName: response?.data.assetName })
      );
    },
    onError: () => {
      showToast(ToastType.ERROR, t('assetError'));
    }
  });

  const { mutate: getTextOverlayByAssetId } = useGetTextOverlayByAssetIdMutation({
    onSuccess: (response) => {
      const textOverlayId = response?.data?.id;
      const organizationId = response?.data?.asset?.organizationId;
      if (textOverlayId && organizationId) {
        navigate(
          PRIVATE_PAGE_ROUTES.EDIT_TEXT_OVERLAY_FOR_ORGANIZATION.replace(
            ':organizationId',
            String(organizationId)
          ).replace(':textOverlayId', String(textOverlayId))
        );
      }
    },
    onError: () => {
      showToast(ToastType.ERROR, t('somethingWentWrong'));
    }
  });

  const {
    data: allOrganizations,
    fetchNextPage,
    hasNextPage
  } = useGetAllOrganizations(
    PAGE_SIZE,
    searchOrganization,
    undefined,
    hasPermissionViewOrganizations,
    currentUserData?.id
  );

  const { mutate: deleteAsset } = useDeleteAssetMutation(queryClient, {
    onSuccess: () => {
      organizationAssetsRefetch();
      showToast(ToastType.SUCCESS, t('assetSuccessfullyDeleted'));
    },
    onError: () => {
      showToast(ToastType.ERROR, t('assetDeleteError'));
    }
  });

  const { mutateAsync: duplicateTextOverlay } = useDuplicateTextOverlayMutation(queryClient, {
    onSuccess: (response?: AxiosResponse) => {
      organizationAssetsRefetch();
      showToast(
        ToastType.SUCCESS,
        t('assetSuccessfullyCreated', { assetName: response?.data.asset?.assetName })
      );
    },
    onError: () => {
      showToast(ToastType.ERROR, t('assetError'));
    }
  });

  const uploadOrganizationId = String(getOrganizationValue() ?? orgSelectValue?.value ?? '');

  const { uploadFiles, isUploading } = useFileUpload({
    organizationId: uploadOrganizationId,
    pickerTypes: UPLOAD_PICKER_TYPES.MEDIA,
    isPublic: false,
    assetScope: AssetScope.ORGANIZATION
  });

  const handleUploadFiles = async (files: File[]) => {
    setHasStartedBatchUpload(true);
    await uploadFiles(files);
    organizationAssetsRefetch();
  };

  const { openFilePicker, hiddenInputProps } = useUploadFilePicker({
    pickerTypes: UPLOAD_PICKER_TYPES.MEDIA,
    multiple: true,
    disabled: isUploading,
    onFilesSelected: handleUploadFiles
  });
  const selectedEventId = ceremonySelectValue?.value ? String(ceremonySelectValue.value) : '';
  const allowedAssetTypes = Object.values(AssetType)
    .filter(
      (type) =>
        type !== AssetType.TEXT && type !== AssetType.STOCK_CLIPS && typeof type === 'number'
    )
    .map((type) => type.toString());

  const {
    data: ceremonyAssets,
    isLoading: isCeremonyAssetsLoading,
    fetchNextPage: ceremonyAssetsFetchNextPage,
    isFetchingNextPage: isCeremonyAssetsFetchingNextPage,
    hasNextPage: ceremonyAssetsHasNextPage
  } = useGetEventAssets(selectedEventId, [], PAGE_SIZE, undefined, allowedAssetTypes);

  const {
    data: eventsData,
    isLoading: isEventsLoading,
    refetch: refetchEvents
  } = useGetAllEvents(
    PAGE_SIZE,
    '',
    undefined,
    undefined,
    !!selectedOrgId,
    selectedOrgId ? [selectedOrgId] : undefined,
    true
  );

  const getOrganizationOptions = () => {
    return (
      allOrganizations?.pages.flatMap((page) =>
        page.data?.items.map((organization: Organization) => ({
          value: organization.id,
          label: organization.name
        }))
      ) || []
    );
  };

  const getCeremonyOptions = (): SelectOptionProps[] => {
    if (!eventsData?.pages) {
      return [];
    }
    return eventsData.pages
      .flatMap((page) => page.data?.items)
      .map((event: Event) => ({
        value: event.id,
        label: event.name
      }));
  };

  const {
    data: organizationAssetsData,
    isLoading: isOrganizationAssetsLoading,
    refetch: organizationAssetsRefetch,
    fetchNextPage: organizationAssetsFetchNextPage,
    isFetchingNextPage: isOrganizationAssetsFetchingNextPage,
    hasNextPage: organizationAssetsHasNextPage
  } = useGetOrganizationAssets(
    PAGE_SIZE,
    true,
    getOrganizationValue(),
    search,
    currentAssetType ? [currentAssetType] : undefined,
    'CreatedAt',
    false,
    currentUserData?.id
  );

  useEffect(() => {
    if (inView) {
      if (ceremonyAssetsHasNextPage) {
        ceremonyAssetsFetchNextPage();
      } else if (organizationAssetsHasNextPage) {
        organizationAssetsFetchNextPage();
      }
    }
  }, [inView, ceremonyAssetsHasNextPage, organizationAssetsHasNextPage]);

  useEffect(() => {
    if (!dropdownOpen) {
      return;
    }
    const handleClick = (e: MouseEvent | TouchEvent) => {
      if (showOrgSelectModal) {
        return;
      }
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [dropdownOpen, showOrgSelectModal]);

  useEffect(() => {
    if (inView && organizationAssetsHasNextPage) {
      organizationAssetsFetchNextPage();
    }
  }, [inView, organizationAssetsHasNextPage]);

  useEffect(() => {
    if (!hasStartedBatchUpload || isUploading) {
      return;
    }

    setDropdownOpen(false);
    setShowOrgSelectModal(false);
    setOrgSelectValue(null);
    organizationAssetsRefetch();
    setHasStartedBatchUpload(false);
  }, [hasStartedBatchUpload, isUploading]);

  const deleteSelectedAsset = async (asset: Asset) => {
    deleteAsset(asset.id);
  };

  const searchCallback = (query: string) => {
    setSearch(query);
  };

  const handleCurrentTab = (tab: TABS) => {
    setCurrentTab(tab);
    switch (tab) {
      case TABS.ALL_ASSET:
        setCurrentAssetType('');
        break;
      case TABS.PHOTO:
        setCurrentAssetType(String(AssetType.IMAGE));
        break;
      case TABS.VIDEO:
        setCurrentAssetType(String(AssetType.VIDEO));
        break;
      case TABS.MUSIC:
        setCurrentAssetType(String(AssetType.AUDIO));
        break;
      case TABS.TEXT_OVERLAY:
        setCurrentAssetType(String(AssetType.TEXT_OVERLAY));
        break;
      default:
        setCurrentAssetType('');
    }
  };

  const handleAddSelectedAssets = async () => {
    if (!hasMediaLibraryAccess) {
      return;
    }
    if (selectedCeremonyAssets.length > 0) {
      for (const asset of selectedCeremonyAssets) {
        const type = getAssetTypeForOrganizationAsset(asset);
        const payload: AssetClonePayload = {
          assetId: asset.id,
          organizationId: Number(orgSelectValue?.value),
          assetType: type
        };
        createAssetClone(payload);
      }
      setOrgSelectValue(null);
      setCeremonySelectValue(null);
      setSelectedOrgId(null);
      setSelectedCeremonyAssets([]);
      setChooseCeremonyModalOpen(false);
    }
  };

  const handleEditTextOverlay = (selectedAsset: Asset) => {
    if (selectedAsset?.id) {
      getTextOverlayByAssetId(selectedAsset.id);
    }
  };

  const handleDuplicateClick = (asset: Asset) => {
    setSelectedAssetForDuplicate(asset);
    setShowDuplicateModal(true);
  };

  const handleDuplicateAsset = async () => {
    if (!selectedAssetForDuplicate) {
      return;
    }

    await duplicateTextOverlay(selectedAssetForDuplicate.id);

    setShowDuplicateModal(false);
    setSelectedAssetForDuplicate(null);
  };

  return (
    <div className='flex w-full flex-col p-6 px-12'>
      <input {...hiddenInputProps} />
      {isOrganizationAssetsLoading ? (
        <Spinner />
      ) : (
        <>
          <div className='mb-4 flex w-full flex-col justify-between md:flex-row md:items-center'>
            <Label text={t('organization-assets')} className={'left-0 mb-4'} type={LABEL_TYPE.H1} />
            <div className='flex w-full flex-row justify-between gap-x-4 md:w-1/2'>
              <div className='flex-1'>
                <Search callback={searchCallback} />
              </div>
              <div className='relative w-auto' ref={dropdownRef}>
                <Button
                  className='flex w-auto flex-row items-center justify-between gap-x-1'
                  variant={ButtonVariant.PRIMARY}
                  handleOnClick={() => setDropdownOpen((prev) => !prev)}>
                  <Label
                    text={t('addMedia')}
                    type={LABEL_TYPE.PARAGRAPH1_BOLD}
                    className='text-primary-white'
                  />
                  <span className='ml-1'>
                    {dropdownOpen ? (
                      <ArrowUpIcon className='fill-primary-white' />
                    ) : (
                      <ArrowDownIcon className='fill-primary-white' />
                    )}
                  </span>
                </Button>
                {dropdownOpen && (
                  <div className='absolute right-0 z-10 mt-2 w-max min-w-full rounded-md border border-secondary-blue-gray bg-white shadow-lg'>
                    <div className='flex flex-col'>
                      <div
                        className='flex cursor-pointer items-center justify-between border-b border-secondary-blue-gray px-4 py-2 hover:rounded-t-md hover:bg-secondary-light-blue-gray'
                        onClick={() => {
                          if (getOrganizationValue() === undefined) {
                            setShowOrgSelectModal(true);
                          } else if (!isBlockedWithoutMediaLibrary()) {
                            openFilePicker();
                          }
                        }}>
                        <span className='flex items-center gap-2'>
                          {isUploading ? (
                            <>
                              <Spinner className='size-4' />
                              <span className='text-primary-blue'>{t('uploading')}</span>
                            </>
                          ) : (
                            <>{t('uploadNew')}</>
                          )}
                        </span>
                      </div>
                      <div
                        className='flex cursor-pointer items-center justify-between border-b border-secondary-blue-gray px-4 py-2 hover:rounded-b-md hover:bg-secondary-light-blue-gray'
                        onClick={() => {
                          setDropdownOpen(false);
                          if (getOrganizationValue() === undefined) {
                            setShowOrgSelectModal(true);
                            setTextOverlayOptionSelected(true);
                          } else if (!isBlockedWithoutMediaLibrary()) {
                            navigate(
                              PRIVATE_PAGE_ROUTES.CREATE_TEXT_OVERLAY_FOR_ORGANIZATION.replace(
                                ':organizationId',
                                String(getOrganizationValue())
                              )
                            );
                          }
                        }}>
                        <span>{t('addOverlayText')}</span>
                      </div>
                      <div
                        className='flex cursor-pointer items-center justify-between border-b border-secondary-blue-gray px-4 py-2 hover:rounded-b-md hover:bg-secondary-light-blue-gray'
                        onClick={() => {
                          setDropdownOpen(false);
                          setChooseCeremonyModalOpen(true);
                        }}>
                        <span>{t('addFromCeremony')}</span>
                      </div>
                      <div
                        className='flex cursor-pointer items-center justify-between px-4 py-2 hover:rounded-b-md hover:bg-secondary-light-blue-gray'
                        onClick={() => {
                          setDropdownOpen(false);
                          if (
                            getOrganizationValue() !== undefined &&
                            isBlockedWithoutMediaLibrary()
                          ) {
                            return;
                          }
                          setIsSearchMusicModalOpen(true);
                        }}>
                        <span>{t('searchForMusic')}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div
            className={
              'no-scrollbar mt-4 flex flex-row overflow-x-scroll md:mt-8 lg:overflow-x-hidden'
            }>
            {Object.values(TABS).map((tab) => (
              <div
                key={tab}
                className={clsx(
                  'cursor-pointer whitespace-nowrap rounded px-5 py-2.5',
                  currentTab === tab && 'bg-secondary-pale-blue-gray font-bold'
                )}
                onClick={() => {
                  handleCurrentTab(tab);
                }}>
                {t(tab)}
              </div>
            ))}
          </div>
          <div className='w-full border-b' />

          <Modal
            showDownloadIcon={false}
            modalClasses={clsx(
              'max-w-full',
              selectedAsset?.assetType === IMAGE
                ? 'max-h-[98%] min-w-full md:max-w-[1200px] lg:min-w-[1200px]'
                : 'max-h-[75%] min-w-[300px] md:max-w-[600px] lg:min-w-[600px]'
            )}
            height='h-full'
            isOpen={showAsset}
            label={assetDetailsLabel}
            onClose={() => setShowAsset(false)}>
            {showAsset && (
              <AssetDetails
                asset={selectedAsset}
                setAssetDetailsLabel={setAssetDetailsLabel as Dispatch<SetStateAction<string>>}
                closeModal={() => setShowAsset(false)}
              />
            )}
          </Modal>

          <div
            className={
              'mt-6 grid grid-cols-2 gap-4 overflow-y-auto scrollbar-none sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'
            }
            style={{ maxHeight: 'calc(100vh - 250px)' }}>
            {organizationAssetsData?.pages
              .flatMap((page) => page.data?.items)
              .map((asset: Asset) => (
                <AssetItem
                  setSelectedAsset={
                    setSelectedAsset as Dispatch<SetStateAction<Asset | ThemeProp | null>>
                  }
                  setShowAsset={setShowAsset}
                  deleteSelectedAsset={deleteSelectedAsset}
                  asset={asset}
                  key={asset.id}
                  assetType={asset.assetType}
                  selectedVideoUrl={''}
                  canBeEdited={asset.assetType === TEXT_OVERLAY}
                  editSelectedAsset={handleEditTextOverlay}
                  canBeDuplicated={asset.assetType === TEXT_OVERLAY}
                  onDuplicateAssetClick={handleDuplicateClick}
                />
              ))}
            <div ref={ref} className='size-1'></div>
          </div>
          {!chooseCeremonyModalOpen &&
            (isOrganizationAssetsFetchingNextPage || organizationAssetsHasNextPage) && (
              <PaginationArrow
                isFetchingNextPage={isOrganizationAssetsFetchingNextPage}
                inView={inView}
                className='w-full bg-white'
              />
            )}
          {isSearchMusicModalOpen && (
            <SearchMusicModal
              isOpen={isSearchMusicModalOpen}
              onClose={() => setIsSearchMusicModalOpen(false)}
              organizationId={getOrganizationValue()}
              onSave={() => organizationAssetsRefetch()}
              organizationOptions={getOrganizationOptions()}
            />
          )}

          {showOrgSelectModal && (
            <Modal
              isOpen={!!showOrgSelectModal}
              onClose={() => {
                setOrgSelectValue(null);
                setShowOrgSelectModal(false);
              }}
              label={t('selectOrganization')}
              showDownloadIcon={false}
              height='h-full'
              modalClasses='w-full md:w-[400px] h-[300px] border border-secondary-blue-gray'>
              <div className='flex flex-col gap-4 p-2'>
                <SingleSelect
                  id='org-select-upload'
                  options={getOrganizationOptions()}
                  value={orgSelectValue}
                  handleChange={(option: SelectOptionProps | null) => setOrgSelectValue(option)}
                  isSearchable={true}
                  isClearable={true}
                  isRequired={true}
                  label={t('organization')}
                  placeholder={t('organization')}
                  handleInputChange={setSearchOrganization}
                  fetchNextPage={fetchNextPage}
                  hasNextPage={hasNextPage}
                  className='w-full'
                  errorMessage={mediaLibraryMissingMessage}
                  isInvalid={!!mediaLibraryMissingMessage}
                />
                <Button
                  className='mt-4'
                  variant={ButtonVariant.PRIMARY}
                  handleOnClick={() => {
                    if (orgSelectValue) {
                      setDropdownOpen(true);
                      setTimeout(() => {
                        if (showOrgSelectModal) {
                          if (textOverlayOptionSelected) {
                            navigate(
                              PRIVATE_PAGE_ROUTES.CREATE_TEXT_OVERLAY_FOR_ORGANIZATION.replace(
                                ':organizationId',
                                String(orgSelectValue.value)
                              )
                            );
                          } else {
                            openFilePicker();
                          }
                        }
                      }, 100);
                    }
                  }}
                  disabled={!orgSelectValue || !hasMediaLibraryAccess}>
                  {t('continue')}
                </Button>
              </div>
            </Modal>
          )}

          {chooseCeremonyModalOpen && (
            <Modal
              isOpen={chooseCeremonyModalOpen}
              onClose={() => {
                setCeremonySelectValue(null);
                setOrgSelectValue(null);
                setChooseCeremonyModalOpen(false);
                setSelectedCeremonyAssets([]);
                setSelectedOrgId(null);
              }}
              label={t('selectCeremony')}
              showDownloadIcon={false}
              height='h-full'
              modalClasses='w-full h-full md:w-[600px] md:h-[600px] lg:h-[800px] lg:w-[800px] border border-secondary-blue-gray'
              hasButton={true}
              containerClasses='flex flex-col !overflow-y-hidden !overflow-x-hidden'
              hasCancelButton
              buttonClasses='!my-2 !mr-0 flex justify-end'
              buttonText={t('add')}
              buttonType={ButtonVariant.PRIMARY}
              isButtonDisabled={
                selectedCeremonyAssets.length === 0 ||
                !ceremonySelectValue ||
                !orgSelectValue ||
                !hasMediaLibraryAccess
              }
              handleSubmit={() => {
                setDropdownOpen(false);
                handleAddSelectedAssets();
              }}>
              <div className='flex h-full flex-col gap-3'>
                {(() => {
                  const orgOptions = getOrganizationOptions();
                  if (orgOptions.length === 1) {
                    if (!orgSelectValue || orgSelectValue.value !== orgOptions[0].value) {
                      setOrgSelectValue(orgOptions[0]);
                      setSelectedOrgId(orgOptions[0].value as number);
                    }
                    return mediaLibraryMissingMessage ? (
                      <Label
                        text={mediaLibraryMissingMessage}
                        type={LABEL_TYPE.PARAGRAPH2}
                        className='!text-accent-red'
                      />
                    ) : (
                      <></>
                    );
                  }
                  return (
                    <SingleSelect
                      id='org-select-upload'
                      options={orgOptions}
                      value={orgSelectValue}
                      handleChange={(option: SelectOptionProps | null) => {
                        if (option === null) {
                          setOrgSelectValue(null);
                          setCeremonySelectValue(null);
                          setSelectedOrgId(null);
                        } else {
                          setOrgSelectValue(option);
                          setCeremonySelectValue(null);
                          setSelectedOrgId(option.value as number);
                          refetchEvents();
                        }
                      }}
                      isSearchable={true}
                      isClearable={true}
                      isRequired={true}
                      label={t('organization')}
                      placeholder={t('organization')}
                      handleInputChange={setSearchOrganization}
                      fetchNextPage={fetchNextPage}
                      hasNextPage={hasNextPage}
                      className='w-full'
                      errorMessage={mediaLibraryMissingMessage}
                      isInvalid={!!mediaLibraryMissingMessage}
                    />
                  );
                })()}

                <SingleSelect
                  id='org-select-upload-ceremony'
                  options={getCeremonyOptions()}
                  value={ceremonySelectValue}
                  handleChange={(option: SelectOptionProps | null) => {
                    if (option === null) {
                      setCeremonySelectValue(null);
                    } else {
                      setCeremonySelectValue(option);
                    }
                  }}
                  isSearchable={true}
                  isClearable={true}
                  isRequired={true}
                  label={t('ceremony')}
                  placeholder={t('ceremony')}
                  handleInputChange={setSearchOrganization}
                  fetchNextPage={fetchNextPage}
                  hasNextPage={hasNextPage}
                  className='w-full'
                  disabled={!orgSelectValue || isEventsLoading}
                />

                {ceremonySelectValue && (
                  <>
                    {isCeremonyAssetsLoading ? (
                      <Spinner />
                    ) : (
                      <div className='mt-1 grid grid-cols-2 gap-4 overflow-y-auto scrollbar-none sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'>
                        {ceremonyAssets?.pages
                          .flatMap((page) => page.data?.items)
                          .map((asset: Asset) => (
                            <div key={asset.id} className='group relative'>
                              <div
                                onClick={() => {
                                  setSelectedCeremonyAssets((prev) =>
                                    prev.some((a) => a.id === asset.id)
                                      ? prev.filter((a) => a.id !== asset.id)
                                      : [...prev, asset]
                                  );
                                }}>
                                <AssetItem
                                  canBeDeleted={false}
                                  canBeEdited={false}
                                  asset={asset}
                                  assetType={asset.assetType}
                                  selectedVideoUrl=''
                                  multiselect={true}
                                  selectedAssets={selectedCeremonyAssets}
                                />
                              </div>
                            </div>
                          ))}

                        <div ref={ref} className='size-1' />
                      </div>
                    )}

                    <PaginationArrow
                      isFetchingNextPage={isCeremonyAssetsFetchingNextPage}
                      inView={inView}
                      className='w-full shrink-0 bg-white'
                    />
                  </>
                )}
              </div>
            </Modal>
          )}

          {showDuplicateModal && selectedAssetForDuplicate && (
            <ConfirmationModal
              isOpen={showDuplicateModal}
              onClose={() => {
                setShowDuplicateModal(false);
                setSelectedAssetForDuplicate(null);
              }}
              onConfirm={() => {
                handleDuplicateAsset();
              }}
              message={t('duplicateAssetQuestion', {
                assetName: selectedAssetForDuplicate.assetName
              })}
            />
          )}
        </>
      )}
    </div>
  );
};
